import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";

/**
 * Snackbar — Ocean Blue Design System
 *
 * A top-centre message that does not block the page, replacing the browser's
 * native alert(). The dialog alert() produces is unstyled, freezes the tab, and
 * renders a long message badly — the cash-channel refusal is 150+ characters:
 *
 *   "The branch holds Rs.20,000.00 in cash, so Rs.40,000.00 cannot be paid out
 *    in cash. Rs.1,20,000.00 is in the bank — pay this online instead, or
 *    withdraw the cash from the bank first."
 *
 * Props:
 *  isOpen   : boolean
 *  onClose  : () => void
 *  message  : string
 *  variant  : "error" | "success" | "warning" | "info"   (default "error")
 *  duration : ms before auto-dismiss. 0 = stay until dismissed.
 *             Omit it and the duration is DERIVED FROM LENGTH (see below).
 */

const VARIANTS = {
  error: {
    bar: "bg-danger-50 border-danger-200",
    icon: "text-danger-600",
    text: "text-danger-900",
    glyph: "M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z",
  },
  success: {
    bar: "bg-success-50 border-success-200",
    icon: "text-success-600",
    text: "text-success-900",
    glyph: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
  },
  warning: {
    bar: "bg-warning-50 border-warning-200",
    icon: "text-warning-600",
    text: "text-warning-900",
    glyph: "M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z",
  },
  info: {
    bar: "bg-info-50 border-info-200",
    icon: "text-info-600",
    // info-800, NOT info-900: the info palette stops at 800. An undefined
    // Tailwind token emits no class and no warning, so text-info-900 would have
    // rendered as unstyled text on a pale blue bar — invisible, and silent.
    text: "text-info-800",
    glyph: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  },
};

/**
 * Reading time, not a fixed timer.
 *
 * A flat 3s is fine for "Saved" and far too short for the channel-guard message,
 * which the operator has to read, understand and act on. ~18 chars/second is a
 * slow-but-safe reading rate; the floor keeps short messages on screen long
 * enough to notice, the ceiling stops a long one lingering.
 */
const readingTime = (message) => {
  const chars = String(message || "").length;
  return Math.min(10000, Math.max(4000, Math.round((chars / 18) * 1000)));
};

const Snackbar = ({
  isOpen,
  onClose,
  message,
  variant = "error",
  duration,
}) => {
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const ms = duration === undefined ? readingTime(message) : duration;

  useEffect(() => {
    // 0 means "stay until dismissed". Paused means the operator is reading it —
    // a message must never vanish mid-sentence under the cursor.
    if (!isOpen || ms === 0 || paused) return undefined;
    timerRef.current = setTimeout(() => onClose?.(), ms);
    return () => clearTimeout(timerRef.current);
  }, [isOpen, ms, paused, onClose, message]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  if (!isOpen || !message) return null;

  const v = VARIANTS[variant] || VARIANTS.error;

  return createPortal(
    <div
      // Positioned and stacked with an INLINE STYLE, not Tailwind classes.
      //
      // Two failures forced this, both seen on screen:
      //
      // 1. HIDDEN BEHIND THE MODAL. tailwind.config.js defines a NAMED zIndex
      //    scale, so `z-[10000]` emits no class at all — and Modal.jsx's own
      //    `z-[9999]` is dead for the same reason, leaving it at z-index:auto.
      //    Both portal to document.body, so DOM order decided and the modal
      //    won. `z-toast` compiles, but --z-toast is 80 and a real number
      //    always beats `auto` only when the other element has none; relying on
      //    that is too subtle to leave to a token. A literal wins outright.
      //
      // 2. CLIPPED AT THE TOP. `top-4` put it under the fixed app header, which
      //    is taller than the 60px --header-h suggests once the breadcrumb bar
      //    is counted. This clears any header without depending on its height.
      className="fixed left-1/2 -translate-x-1/2 w-[min(92vw,34rem)] pointer-events-none"
      // 16px from the very top of the VIEWPORT, deliberately over the app
      // header rather than below it.
      //
      // Below the header (132px, clearing PageLayout's 120px of chrome) reads
      // better on a plain page, but a Modal is centred with max-h-[90vh] — so
      // it begins around 5vh down and the snackbar would land ON the dialog it
      // is reporting about. These messages are usually RAISED BY a modal (the
      // withdrawal form is one), so clearing the modal matters more than
      // clearing the header. The high z-index below keeps it readable there.
      style={{ top: "16px", zIndex: 2147483000 }}
      // Announced to screen readers without stealing focus, which is the whole
      // point of not using alert().
      role="status"
      aria-live="polite"
    >
      <div
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        className={[
          "pointer-events-auto flex items-start gap-3 rounded-xl border px-4 py-3",
          "shadow-lg animate-fade-in",
          v.bar,
        ].join(" ")}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="2"
          stroke="currentColor"
          className={`w-5 h-5 shrink-0 mt-0.5 ${v.icon}`}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d={v.glyph} />
        </svg>

        {/* min-w-0 so a long unbroken message wraps instead of forcing the box
            wider than the viewport. */}
        <p className={`min-w-0 flex-1 text-sm leading-snug ${v.text}`}>
          {message}
        </p>

        <button
          type="button"
          onClick={() => onClose?.()}
          aria-label="Dismiss"
          className={`shrink-0 -mr-1 -mt-0.5 p-1 rounded-md opacity-60 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-current transition-opacity ${v.icon}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-4 h-4">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>,
    document.body
  );
};

/**
 * useSnackbar — keeps each call site a ONE-LINE change.
 *
 *   const { showError, showSuccess, SnackbarHost } = useSnackbar();
 *   showError(backendError);            // was: alert(backendError)
 *   ...
 *   <SnackbarHost />                    // once, anywhere in the page's JSX
 *
 * Without this, replacing 57 alert() calls would mean three useState
 * declarations per page and a JSX block per call site.
 */
export const useSnackbar = () => {
  const [snack, setSnack] = useState({ isOpen: false, message: "", variant: "error" });

  const show = useCallback((message, variant = "error") => {
    // Re-open with a fresh identity so a second message restarts the timer
    // rather than inheriting the first one's remaining time.
    setSnack({ isOpen: false, message: "", variant });
    requestAnimationFrame(() => setSnack({ isOpen: true, message: String(message ?? ""), variant }));
  }, []);

  const hide = useCallback(() => setSnack((s) => ({ ...s, isOpen: false })), []);

  const showError = useCallback((m) => show(m, "error"), [show]);
  const showSuccess = useCallback((m) => show(m, "success"), [show]);
  const showWarning = useCallback((m) => show(m, "warning"), [show]);
  const showInfo = useCallback((m) => show(m, "info"), [show]);

  const SnackbarHost = useCallback(
    () => (
      <Snackbar
        isOpen={snack.isOpen}
        message={snack.message}
        variant={snack.variant}
        onClose={hide}
      />
    ),
    [snack, hide]
  );

  return { snack, show, hide, showError, showSuccess, showWarning, showInfo, SnackbarHost };
};

export default Snackbar;
